import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { TrackingService } from '../TrackingService';
import { StatePreferenceService } from '../StatePreferenceService';
import { describeSubscription } from '../utils/subscriptionPresentation';
import { formatDateOnly } from '../utils/dateOnly';
import { Link } from 'react-router-dom';
import { config } from '../Constants';
import {
    Container, Typography, Box, Paper, Grid, Avatar,
    Button, Card, CardContent, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    DialogContentText, CircularProgress, Snackbar, Alert
} from '@mui/material';
import {
    WorkspacePremium as PremiumIcon,
    ShoppingCart, Email
} from '@mui/icons-material';
import StateSelector from '../components/StateSelector';
import ThemePreferenceSelector from '../components/ThemePreferenceSelector';

const URL = config.url;

const ProfilePage = () => {
    const { user, isAuthenticated, authToken, validateToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [trackedCount, setTrackedCount] = useState(0);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    const [selectedStates, setSelectedStates] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [statesSaving, setStatesSaving] = useState(false);
    const [hasStateChanges, setHasStateChanges] = useState(false);
    const [pendingStates, setPendingStates] = useState([]);

    const hasFetchedRef = useRef(false);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const fetchStatePreferences = useCallback(async () => {
        if (!user?.premium) return;

        try {
            setStatesLoading(true);
            const [preferences, available] = await Promise.all([
                StatePreferenceService.getUserStatePreferences(),
                StatePreferenceService.getAllAvailableStates()
            ]);

            setAllStates(available);
            
            if (preferences && preferences.length > 0) {
                setSelectedStates(preferences);
                setPendingStates(preferences);
            }
        } catch (error) {
            console.error('Error fetching state preferences:', error);
            showSnackbar('Failed to load state preferences', 'error');
        } finally {
            setStatesLoading(false);
        }
    }, [user?.premium]);

    useEffect(() => {
        if (user?.subscriptionStatus) {
            setSubscriptionStatus(user.subscriptionStatus);
        }
        if (user?.selectedStates) {
            setSelectedStates(user.selectedStates);
            setPendingStates(user.selectedStates);
        }
    }, [user?.subscriptionStatus, user?.selectedStates]);

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!isAuthenticated || hasFetchedRef.current) return;

            try {
                setLoading(true);
                hasFetchedRef.current = true;

                const trackings = await TrackingService.getUserTrackedStocks();
                setTrackedCount(trackings.length);

                if (user?.premium) {
                    await fetchStatePreferences();
                }
            } catch (error) {
                console.error('Error fetching initial data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [isAuthenticated, user?.premium, fetchStatePreferences]);

    const handleCancelSubscription = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(`${URL}/api/v1/subscription/cancel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                setSubscriptionStatus("canceling");
                showSnackbar('Your subscription has been canceled. You will have premium access until the end of your billing period.', 'success');
                setCancelDialogOpen(false);
                
                setTimeout(async () => {
                    await validateToken();
                }, 1000);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error canceling subscription:', error);
            showSnackbar(error.message || 'Failed to cancel subscription. Please try again.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleResumeSubscription = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(`${URL}/api/v1/subscription/resume`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                setSubscriptionStatus("active");
                showSnackbar('Your subscription has been resumed. Your premium benefits will continue as normal.', 'success');
                setResumeDialogOpen(false);
                
                setTimeout(async () => {
                    await validateToken();
                }, 1000);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to resume subscription');
            }
        } catch (error) {
            console.error('Error resuming subscription:', error);
            showSnackbar(error.message || 'Failed to resume subscription. Please try again.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleStateChange = (newStates) => {
        setPendingStates(newStates);
        setHasStateChanges(true);
    };

    const handleSaveStatePreferences = async () => {
        setStatesSaving(true);
        try {
            const stateIds = pendingStates.map(s => s.stateId);
            await StatePreferenceService.updateUserStatePreferences(stateIds);
            
            setSelectedStates(pendingStates);
            setHasStateChanges(false);
            showSnackbar(
                `Successfully updated your notification preferences for ${pendingStates.length} state${pendingStates.length !== 1 ? 's' : ''}`,
                'success'
            );
        } catch (error) {
            console.error('Error saving state preferences:', error);
            showSnackbar('Failed to save state preferences. Please try again.', 'error');
        } finally {
            setStatesSaving(false);
        }
    };

    const handleCancelStateChanges = () => {
        setPendingStates(selectedStates);
        setHasStateChanges(false);
    };

    if (!isAuthenticated) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Please sign in to view your profile
                    </Typography>
                </Paper>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    const {
        isPremiumActive,
        isCanceling: isSubscriptionCanceling,
        isComplimentary,
        canManageSubscription,
        expiryLabel
    } = describeSubscription(user, subscriptionStatus);
    const premiumExpiryDate = user?.premiumExpiry;

    const formattedPremiumExpiryDate = formatDateOnly(premiumExpiryDate);

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    My Profile
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={2}>
                        <Avatar
                            src={user?.imageUrl}
                            alt={user?.name}
                            sx={{ width: 100, height: 100, mx: 'auto' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={10}>
                        <Typography variant="h5">{user?.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Email sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body1" color="text.secondary">
                                {user?.email}
                            </Typography>
                        </Box>
                        {isPremiumActive && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    icon={<PremiumIcon />}
                                    label="Premium Member"
                                    color="primary"
                                    sx={{ mr: 1 }}
                                />
                                {isSubscriptionCanceling && (
                                    <Chip
                                        label="Canceling"
                                        color="error"
                                        size="small"
                                    />
                                )}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Subscription
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PremiumIcon sx={{ mr: 1, color: isPremiumActive ? 'gold' : 'text.secondary' }} />
                                <Typography>
                                    {isPremiumActive ? 'Premium' : 'Free User'}
                                </Typography>
                            </Box>

                            {isPremiumActive && formattedPremiumExpiryDate && (
                                <Typography variant="body2" color="text.secondary">
                                    {expiryLabel}: {formattedPremiumExpiryDate}
                                </Typography>
                            )}

                            {/* A complimentary grant has no Stripe subscription behind it: nothing to cancel, nothing to upgrade. */}
                            {isComplimentary && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Complimentary membership. Every premium feature is on.
                                </Typography>
                            )}
                            {!isComplimentary && (
                            <Box sx={{ mt: 2 }}>
                                {actionLoading ? (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        disabled
                                    >
                                        <CircularProgress size={24} />
                                    </Button>
                                ) : canManageSubscription ? (
                                    isSubscriptionCanceling ? (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setResumeDialogOpen(true)}
                                            fullWidth
                                        >
                                            Resume Subscription
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => setCancelDialogOpen(true)}
                                            fullWidth
                                        >
                                            Cancel Subscription
                                        </Button>
                                    )
                                ) : (
                                    <Button
                                        variant="outlined"
                                        component={Link}
                                        to="/subscription"
                                        fullWidth
                                    >
                                        Upgrade to Premium
                                    </Button>
                                )}
                            </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Tracked Products
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ShoppingCart sx={{ mr: 1 }} />
                                <Typography>
                                    {trackedCount} product{trackedCount !== 1 ? 's' : ''} tracked
                                </Typography>
                            </Box>
                            {isPremiumActive && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Track unlimited products with your premium membership
                                </Typography>
                            )}
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/my-tracked-stocks"
                                sx={{ mt: 2 }}
                                fullWidth
                            >
                                View Tracked Products
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Appearance
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose how StockMaid looks. System follows your device setting.
                    </Typography>
                    <ThemePreferenceSelector />
                </CardContent>
            </Card>

            {isPremiumActive && (
                <StateSelector
                    selectedStates={pendingStates}
                    allStates={allStates}
                    onChange={handleStateChange}
                    onSave={handleSaveStatePreferences}
                    onCancel={handleCancelStateChanges}
                    loading={statesLoading}
                    saving={statesSaving}
                    hasChanges={hasStateChanges}
                />
            )}

            <Dialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                aria-labelledby="cancel-dialog-title"
                aria-describedby="cancel-dialog-description"
            >
                <DialogTitle id="cancel-dialog-title">
                    Cancel Your Premium Subscription?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cancel-dialog-description">
                        Are you sure you want to cancel your premium subscription?
                        <br /><br />
                        You'll continue to have premium access until the end of your current billing period.
                        After that, you'll be downgraded to a free account and lose access to product tracking.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>
                        Keep Subscription
                    </Button>
                    <Button
                        onClick={handleCancelSubscription}
                        color="error"
                        autoFocus
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : 'Confirm Cancellation'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={resumeDialogOpen}
                onClose={() => setResumeDialogOpen(false)}
                aria-labelledby="resume-dialog-title"
                aria-describedby="resume-dialog-description"
            >
                <DialogTitle id="resume-dialog-title">
                    Resume Your Premium Subscription?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="resume-dialog-description">
                        Are you sure you want to resume your premium subscription?
                        <br /><br />
                        Your subscription will continue as normal and you'll be billed on your next
                        renewal date.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResumeDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResumeSubscription}
                        color="primary"
                        autoFocus
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : 'Resume Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ProfilePage;
